import React, { useCallback, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react';

import { useProjectHasTasks } from '../../hooks/useProjectHasTasks';
import { requestProposalExport } from '../../utils/proposalExport';

import { ActionBarActions, ActionButtonLabel } from './ActionBarActions';
import { ActionBarAnalyticsScope } from './actionBarAnalyticsScope';
import { actionInvocationOrigin } from './actionInvocationOrigin';
import {
  ActionMirrorSource, MirroredAction, actionMirrorScope, invokeMirroredAction, mirrorSnapshot, subscribeActionMirror,
} from './actionMirrorStore';
import { ArtifactShareMenu } from './ArtifactShareMenu';
import { CreditMenuActions } from './CreditMenuActions';
import { PendingDesignAction } from './PendingDesignAction';
import { useActionBarAnalytics } from './useActionBarAnalytics';
import { useGeneratedArtifactShares } from './useGeneratedArtifactShares';
import { MissingPagesButton, offersPageGeneration, useMissingDesignPages } from './useMissingDesignPages';
import { useUnclaimedRewardActions } from './useUnclaimedRewardActions';
import './PlanningStartCreditActions.css';
import './StickyPlanningActions.css';

interface Props { projectId?: string | null; sessionId?: string | null; isReadOnly?: boolean; hidden?: boolean }
const attentionMirrorOwners = new WeakMap<HTMLElement, number>();
const sourceKeys = new WeakMap<HTMLElement, number>();
let nextSourceKey = 0;
const sourceKey = (element: HTMLElement): number => {
  if (!sourceKeys.has(element)) sourceKeys.set(element, ++nextSourceKey);

  return sourceKeys.get(element)!;
};
const claimSourceAttention = (element: HTMLElement): (() => void) => {
  attentionMirrorOwners.set(element, (attentionMirrorOwners.get(element) ?? 0) + 1);
  element.dataset.actionAttentionMirrored = 'true';

  return () => {
    const remaining = (attentionMirrorOwners.get(element) ?? 1) - 1;

    if (remaining > 0) attentionMirrorOwners.set(element, remaining);
    else {
      attentionMirrorOwners.delete(element);
      delete element.dataset.actionAttentionMirrored;
    }
  };
};
const isProposalExport = (action: MirroredAction): boolean => (
  /(?:^|\s)ai-overview-action-btn--export(?:-proposal)?(?:\s|$)/.test(action.stateClasses)
  || /^(?:export proposal|exported)$/i.test(action.label)
);
const isArtifactShare = (action: MirroredAction): boolean => (
  action.button.classList.contains('ai-overview-action-btn--share')
);
const ExportProposalAction = () => <button type="button"
  className="ai-overview-action-btn ai-overview-action-btn--export"
  data-action-placement="secondary" onClick={requestProposalExport}>
  <ActionButtonLabel label="Export proposal" />
</button>;
const MirrorButtons: React.FC<{
  source: ActionMirrorSource; scope: string; exportAvailable: boolean;
  rewardActions: React.ReactNode[]; shareAction: React.ReactNode; missingPagesAction: React.ReactNode;
}> = ({
  source, scope, exportAvailable, rewardActions, shareAction, missingPagesAction,
}) => (
  <ActionBarActions attentionActive={source.attentionActive}
    dataActionChoiceMade={source.actions.some((action) => action.selected)}>
    {rewardActions}
    {shareAction}
    {source.actions.filter((action) => !action.button.matches('.ai-overview-action-btn--claim')
      && !isArtifactShare(action)
      && (!exportAvailable || !isProposalExport(action))).map((action, index) => <button key={`${source.ownerId}:${index}`} type="button"
      className={`ai-overview-action-btn ${action.stateClasses}`} disabled={action.disabled}
      aria-busy={action.busy || undefined} aria-pressed={action.selected || undefined}
      data-attention-primary={action.attentionPrimary || undefined}
      data-action-placement={isProposalExport(action) ? 'secondary' : action.placement}
      title={action.title || action.label}
      onClick={(event) => invokeMirroredAction(scope, source, action, actionInvocationOrigin(event.currentTarget))}>
      {action.busy ? <span className="ai-overview-action-spinner" aria-hidden="true" />
        : action.iconClass && <i className={action.iconClass} aria-hidden="true" />}
      <ActionButtonLabel label={action.label} />
    </button>)}
    {missingPagesAction}
    {exportAvailable && <ExportProposalAction />}
    <CreditMenuActions />
    {source.errors.map((error, index) => <span key={`error:${index}`} className="ai-overview-action-error"
      data-action-layout-message="true" role="alert">{error}</span>)}
    {source.notes?.map((note, index) => <span key={`note:${index}`} className="ai-overview-action-note"
      data-action-layout-message="true" role="status">{note}</span>)}
  </ActionBarActions>
);

const useStickyActionSource = ({ projectId, sessionId, isReadOnly, hidden }: Props) => {
  const scope = actionMirrorScope(projectId, sessionId);
  const subscribe = useCallback((listener: () => void) => subscribeActionMirror(scope, listener), [scope]);
  const snapshot = useCallback(() => mirrorSnapshot(scope), [scope]);
  const source = useSyncExternalStore(subscribe, snapshot, () => null);

  // The composer is the only visible home for actions; source controllers
  // remain mounted with their messages to preserve callbacks and dialogs.
  return { scope, source,
    visible: Boolean(projectId && !isReadOnly && !hidden && source) };
};

// eslint-disable-next-line max-statements
export const StickyPlanningActions: React.FC<Props> = (props) => {
  const { scope, source, visible } = useStickyActionSource(props);
  const [trackingElement, setTrackingElement] = useState<HTMLElement | null>(null);
  const trackingRoot = useMemo(() => ({ current: trackingElement }), [trackingElement]);

  useActionBarAnalytics(trackingRoot, props.isReadOnly || props.hidden ? null : props.projectId, scope);
  // Planning output (SRD, designs, tasks) is enough for a proposal; export sits in both Share and More.
  const exportAvailable = useProjectHasTasks(props.projectId);
  const rewardActions = useUnclaimedRewardActions(props);
  const shares = useGeneratedArtifactShares(
    props.isReadOnly || props.hidden ? null : props.projectId ?? null, undefined,
  );
  // Pages that were never designed stay offered whichever gate or suggestion owns the bar.
  const missingPages = useMissingDesignPages(props.isReadOnly || props.hidden ? null : props.projectId);
  const missingPagesAction = missingPages && props.projectId
    && !offersPageGeneration(source?.actions.map((action) => action.label) ?? [])
    ? <MissingPagesButton projectId={props.projectId} sessionId={props.sessionId} label={missingPages.label} /> : null;
  const shareAction = props.projectId && (shares.artifacts.length > 0 || exportAvailable)
    ? <ArtifactShareMenu key={props.projectId} projectId={props.projectId} actions={shares.artifacts}
      onExportProposal={exportAvailable ? requestProposalExport : undefined} /> : null;

  useLayoutEffect(() => {
    const sourceElement = visible ? source?.element : null;

    return sourceElement ? claimSourceAttention(sourceElement) : undefined;
  }, [source?.element, visible]);

  if (!source && props.projectId && !props.isReadOnly && !props.hidden) {
    return <ActionBarAnalyticsScope.Provider value={scope}>
      <PendingDesignAction projectId={props.projectId} sessionId={props.sessionId}
        analyticsRef={setTrackingElement} analyticsScope={scope} menuActions={<CreditMenuActions />}
        missingPagesAction={missingPagesAction}>
        {rewardActions.length > 0 || exportAvailable || shareAction ? <>
          {rewardActions}
          {shareAction}
          {exportAvailable && <ExportProposalAction />}
        </> : null}
      </PendingDesignAction></ActionBarAnalyticsScope.Provider>;
  }
  if (!visible || !source) return null;

  return <ActionBarAnalyticsScope.Provider value={scope}>
    <section ref={setTrackingElement} data-action-analytics-scope={scope}
      className="sticky-planning-actions ai-overview-action-bar notranslate"
      data-sticky-action-mirror="true" translate="no" data-next-step={source.nextStep}
      data-source-entry-id={source.ownerId || undefined} role="group" aria-label={`Current actions · ${source.ownerLabel}`}>
      <MirrorButtons key={sourceKey(source.element)} source={source} scope={scope} exportAvailable={exportAvailable}
        rewardActions={rewardActions} shareAction={shareAction} missingPagesAction={missingPagesAction} />
    </section></ActionBarAnalyticsScope.Provider>;
};
